import Footer from '@/components/Footer';
import Header from '@/components/Header';
import { Input } from '@/components/ui/input';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Mail, MapPin, Phone } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Textarea = ({ id, name, placeholder, rows, required, value, onChange }) => (
   <textarea
      id={id}
      name={name}
      placeholder={placeholder}
      rows={rows}
      required={required}
      value={value}
      onChange={onChange}
      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
   />
);

const ContactPage = () => {
   const [formData, setFormData] = useState({
      name: '',
      email: '',
      subject: '',
      message: ''
   });

   const handleChange = (e) => {
      const { name, value } = e.target;
      // console.log(`Changing ${name} to ${value}`);
      setFormData({
         ...formData,
         [name]: value
      });
      // console.log('Updated formData:', formData);
   };

   const handleSubmit = async (e) => {
      e.preventDefault();
      try {
         const response = await axios.post('http://localhost:3000/api/contact', formData);
         toast.success(response.data.message, {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
         });
         setFormData({ name: '', email: '', subject: '', message: '' }); // Reset form
      } catch (error) {
         toast.error('Failed to send message. Please try again later.', {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
         });
      }
   };

   const fadeIn = {
      hidden: { opacity: 0 },
      visible: { opacity: 1, transition: { duration: 0.6 } }
   };

   const slideUp = {
      hidden: { y: 20, opacity: 0 },
      visible: {
         y: 0,
         opacity: 1,
         transition: {
            type: "spring",
            stiffness: 100,
            damping: 12
         }
      }
   };

   return (
      <div className="flex flex-col min-h-screen bg-green-50">
         <Header />
         <motion.main
            className="flex-1"
            initial="hidden"
            animate="visible"
            variants={fadeIn}
         >
            <div className="container mx-auto px-4 py-12">
               <motion.h1
                  className="text-4xl font-bold text-green-800 mb-8 text-center"
                  variants={slideUp}
               >
                  Contact Us
               </motion.h1>
               <div className="grid md:grid-cols-2 gap-12">
                  <div>
                     <p className="text-lg mb-6">
                        We're here to help! If you have any questions about our services or need assistance with your permit application, please don't hesitate to reach out to us.
                     </p>
                     <div className="space-y-4">
                        <div className="flex items-center">
                           <MapPin className="h-6 w-6 text-green-600 mr-2" />
                           <span>Boac, Marinduque, Philippines</span>
                        </div>
                        <div className="flex items-center">
                           <Phone className="h-6 w-6 text-green-600 mr-2" />
                           <span>(123) 456-7890</span>
                        </div>
                        <div className="flex items-center">
                           <Mail className="h-6 w-6 text-green-600 mr-2" />
                           <span>info@permittree@gmail.com</span>
                        </div>
                     </div>
                     <div className="mt-8">
                        <h2 className="text-2xl font-semibold mb-4 text-green-800">Office Hours</h2>
                        <p>Monday - Friday: 8:00 AM - 5:00 PM</p>
                        <p>Saturday - Sunday: Closed</p>
                     </div>

                     <div className="mt-8">
                        <h2 className="text-2xl font-semibold mb-4 text-green-800">Our Location</h2>
                        <div className="h-[300px] w-full rounded-lg overflow-hidden border border-gray-300 shadow-sm">
                           <iframe
                              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d969.3350189531033!2d121.82871487532522!3d13.442319134534003!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTPCsDI2JzMyLjMiTiAxMjHCsDQ5JzQzLjQiRQ!5e0!3m2!1sen!2sph!4v1698766567267!5m2!1sen!2sph&z=18&t=m&output=embed&iwloc=near"
                              width="100%"
                              height="100%"
                              style={{ border: 0, borderRadius: '0.5rem' }}
                              allowFullScreen=""
                              loading="lazy"
                              referrerPolicy="no-referrer-when-downgrade"
                              title="DENR PENRO Marinduque Location"
                           ></iframe>
                        </div>
                     </div>
                  </div>
                  <div>
                     <form className="space-y-4" onSubmit={handleSubmit}>
                        <div>
                           <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                           <Input id="name" name="name" placeholder="Your Name" required value={formData.name} onChange={handleChange} />
                        </div>
                        <div>
                           <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                           <Input id="email" name="email" type="email" placeholder="your@email.com" required value={formData.email} onChange={handleChange} />
                        </div>
                        <div>
                           <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                           <Input id="subject" name="subject" placeholder="Subject of your message" required value={formData.subject} onChange={handleChange} />
                        </div>
                        <div>
                           <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                           <Textarea id="message" name="message" placeholder="Your message here" rows={5} required value={formData.message} onChange={handleChange} />
                        </div>
                        <button type="submit" className="w-full bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded">Send Message</button>
                     </form>
                  </div>
               </div>
            </div>
         </motion.main>
         <Footer />
      </div>
   );
};

export default ContactPage;
